import {
  formatActionCardAge,
  homeCardEntryFor,
} from '@littlebird/action-cards';
import { useStores, type SentDelegation } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Empty } from '@/components/ui/EmptyState';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  delegatedToLabel,
  DelegationLine,
  toDelegatedTo,
  useSentDelegationsQuery,
  type DelegatedTo,
} from '@/features/action-card-delegation';
import { cn } from '@/lib/utils';

import { HomeCard } from './HomeCard';

import type { WaitingForYouState } from '../use-waiting-for-you';

type DelegatedListProps = {
  readonly home: WaitingForYouState;
  readonly framed?: boolean;
};

const MissingCardRow = ({
  delegatedTo,
  framed,
}: {
  delegatedTo: DelegatedTo;
  framed: boolean;
}) => (
  <YStack className={cn(framed && 'rounded-2xl')}>
    <XStack
      className={cn(
        'min-h-10 items-center gap-2 px-2 py-2',
        framed && 'min-h-16 gap-4 px-5 py-4'
      )}
    >
      <span className="flex size-6 shrink-0 items-center justify-center text-foreground-muted">
        <Icons.user className="size-4" aria-hidden="true" />
      </span>
      <XStack
        className={cn(
          'min-w-0 flex-1 items-baseline gap-1.5',
          framed && 'flex-col items-stretch gap-0.5'
        )}
      >
        <span
          className={cn(
            'truncate text-sm font-medium text-foreground-subtle',
            framed && 'text-[13px] text-foreground'
          )}
        >
          Action card
        </span>
        <span
          className={cn(
            'truncate text-xs text-foreground-muted',
            framed && 'text-sm'
          )}
        >
          {delegatedToLabel(delegatedTo)}
        </span>
      </XStack>
      <span className="shrink-0 text-[0.6875rem] tabular-nums text-foreground-muted">
        {formatActionCardAge(delegatedTo.updatedAt)}
      </span>
    </XStack>
    <DelegationLine
      note={delegatedTo.note}
      className={cn(
        '-mt-1 pb-2 pl-10 pr-2 text-xs',
        framed && 'pb-4 pl-[3.75rem] pr-5'
      )}
    />
  </YStack>
);

const DelegatedRow = observer(
  ({
    delegation,
    home,
    framed,
  }: {
    delegation: SentDelegation;
    home: WaitingForYouState;
    framed: boolean;
  }) => {
    const { actionCards } = useStores();
    const sourceCard = actionCards.data.get(delegation.sourceCardId);
    const entry = sourceCard ? homeCardEntryFor(sourceCard) : null;
    const delegatedTo = toDelegatedTo(delegation);

    if (!entry)
      return <MissingCardRow delegatedTo={delegatedTo} framed={framed} />;

    return (
      <YStack className="gap-1">
        <HomeCard
          entry={entry}
          home={home}
          surface={framed ? 'web_feeds' : 'web_home'}
        />
        <DelegationLine
          label={delegatedToLabel(delegatedTo)}
          note={delegatedTo.note}
          className="px-5 pb-2 text-xs"
        />
      </YStack>
    );
  }
);

export const DelegatedList = observer(
  ({ home, framed = false }: DelegatedListProps) => {
    const { data: delegations = [], isLoading } = useSentDelegationsQuery();

    if (!isLoading && delegations.length === 0) {
      return framed ? (
        <Empty.Root variant="plain" className="mx-auto w-full max-w-3xl">
          <Empty.Media variant="icon">
            <Icons.user />
          </Empty.Media>
          <Empty.Header>
            <Empty.Title>Nothing delegated yet</Empty.Title>
            <Empty.Description>
              Cards you assign to teammates show up here
            </Empty.Description>
          </Empty.Header>
        </Empty.Root>
      ) : null;
    }

    const rows = delegations.map((delegation) => (
      <DelegatedRow
        key={delegation.id}
        delegation={delegation}
        home={home}
        framed={framed}
      />
    ));

    if (!framed) return <YStack className="no-record">{rows}</YStack>;

    return (
      <YStack className="h-full overflow-y-auto scrollbar-hide">
        <YStack className="mx-auto w-full max-w-3xl gap-1 px-6 pb-[50vh] pt-2">
          <h2 className="px-5 pb-2.5 text-xs font-medium text-foreground-muted">
            Delegated
          </h2>
          {rows}
        </YStack>
      </YStack>
    );
  }
);
